/** Pure rules: integer millilitres and cents avoid threshold rounding errors. */
export function purchaseState(data) {
  if (!data || !Number.isSafeInteger(data.oilMl) || data.oilMl < 0 ||
      !Number.isSafeInteger(data.filterCents) || data.filterCents < 0 ||
      !Number.isSafeInteger(data.items) || data.items < 0 ||
      typeof data.hasOils !== 'boolean' || typeof data.hasFilters !== 'boolean' ||
      typeof data.unknown !== 'boolean' || typeof data.currency !== 'string') throw new Error('Invalid policy snapshot');
  const oilMissing = data.hasOils ? Math.max(0,100000-data.oilMl) : 0;
  const filterMissing = data.hasFilters ? Math.max(0,5000-data.filterCents) : 0;
  const unknown = data.unknown || (data.hasFilters && data.currency !== 'EUR');
  return {oilMissing,filterMissing,unknown,empty:data.items===0,eligible:data.items>0&&!unknown&&oilMissing===0&&filterMissing===0};
}
if (typeof document !== 'undefined') {
  const [{Component},{StandardEvents}] = await Promise.all([import('@theme/component'),import('@shopify/events')]);
  /** @extends {Component<{message: HTMLElement}>} */
  class OrderMinimum extends Component {
    requiredRefs = ['message'];
    connectedCallback() {
      super.connectedCallback();
      this.listeners?.abort();
      this.listeners=new AbortController();
      this.changeVersion=0;
      const options={signal:this.listeners.signal};
      try { this.renderPolicy(JSON.parse(this.querySelector('[data-ofc-policy]').textContent)); }
      catch { this.showError(); }
      document.addEventListener(StandardEvents.cartLinesUpdate,this.onCartChange,options);
      document.addEventListener(StandardEvents.cartDiscountUpdate,this.onCartChange,options);
      document.addEventListener('ofc:cart-updated',this.onCartChange,options);
      document.addEventListener('visibilitychange',this.onVisible,options);
      window.addEventListener('pageshow',this.onVisible,options);
      window.addEventListener('focus',this.onVisible,options);
      document.addEventListener('click',this.guardCheckout,{...options,capture:true});
      document.addEventListener('submit',this.guardSubmit,{...options,capture:true});
      this.resizeObserver=new ResizeObserver(()=>this.measure());
      this.resizeObserver.observe(this);
      this.measure();
      this.refresh();
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      this.listeners?.abort();this.request?.abort();this.resizeObserver?.disconnect();
      clearTimeout(this.timer);clearTimeout(this.requestTimer);this.changeVersion++;
      document.body.style.removeProperty('--ofc-minimum-height');
    }
    onVisible=()=>{if(document.visibilityState==='visible')this.queueRefresh();};
    onCartChange=async(event)=>{
      const version=++this.changeVersion;
      this.ready=false;
      this.updateButtons();
      this.request?.abort();this.request=null;
      try {await event.promise;}catch{}
      if(this.isConnected&&version===this.changeVersion)this.queueRefresh();
    };
    queueRefresh(){clearTimeout(this.timer);this.timer=setTimeout(()=>this.refresh(),120);}
    async refresh(){
      this.request?.abort();clearTimeout(this.requestTimer);
      const request=new AbortController();this.request=request;
      this.requestTimer=setTimeout(()=>request.abort(),12000);
      try{
        const response=await fetch(this.dataset.policyUrl,{signal:request.signal,cache:'no-store'});
        if(!response.ok)throw new Error('Policy unavailable');
        const html=new DOMParser().parseFromString(await response.text(),'text/html');
        const snapshot=html.querySelector('[data-ofc-policy]');
        if(!snapshot)throw new Error('Missing policy snapshot');
        const data=JSON.parse(snapshot.textContent);
        if(request!==this.request||!this.isConnected)return;
        this.renderPolicy(data);
      }catch{
        if(request!==this.request||!this.isConnected)return;
        this.showError();
      }finally{if(request===this.request)clearTimeout(this.requestTimer);}
    }
    renderPolicy(data){
      const state=purchaseState(data);this.ready=state.eligible;
      this.hidden=state.eligible;
      let message=this.dataset.empty;
      const number=new Intl.NumberFormat(this.dataset.locale||'it-IT',{maximumFractionDigits:3});
      const money=new Intl.NumberFormat(this.dataset.locale||'it-IT',{style:'currency',currency:'EUR'});
      if(state.unknown)message=data.hasFilters&&data.currency!=='EUR'?this.dataset.currency:this.dataset.unknown;
      else if(!state.empty){
        const parts=[];
        if(data.hasOils)parts.push(state.oilMissing?this.dataset.oilMissing.replace('[amount]',number.format(state.oilMissing/1000)):this.dataset.oilReached);
        if(data.hasFilters)parts.push(state.filterMissing?this.dataset.filterMissing.replace('[amount]',money.format(state.filterMissing/100)):this.dataset.filterReached);
        message=parts.join(' · ');
      }
      if(this.refs.message.textContent!==message)this.refs.message.textContent=message;
      this.updateButtons();this.measure();
    }
    showError(){this.ready=false;this.hidden=false;this.refs.message.textContent=this.dataset.error;this.updateButtons();this.measure();}
    updateButtons(){
      for(const button of document.querySelectorAll('[data-ofc-checkout]')){
        if(!(button instanceof HTMLButtonElement))continue;
        button.disabled=!this.ready;
        button.setAttribute('aria-disabled',String(!this.ready));
      }
    }
    guardCheckout=(event)=>{
      if(this.ready||!(event.target instanceof Element))return;
      const trigger=event.target.closest('[data-ofc-checkout],a[href]');
      if(!trigger)return;
      const isCheckout=trigger.hasAttribute('data-ofc-checkout')||(trigger instanceof HTMLAnchorElement&&trigger.origin===location.origin&&/\/(checkout|checkouts)(\/|$)/.test(trigger.pathname));
      if(isCheckout){event.preventDefault();event.stopImmediatePropagation();this.hidden=false;this.measure();this.querySelector('a')?.focus();}
    };
    guardSubmit=(event)=>{
      if(event.submitter?.getAttribute('name')==='checkout'&&!this.ready){
        event.preventDefault();event.stopImmediatePropagation();this.hidden=false;this.measure();this.querySelector('a')?.focus();
      }
    };
    measure(){document.body.style.setProperty('--ofc-minimum-height',this.hidden?'0px':Math.ceil(this.getBoundingClientRect().height)+'px');}
  }
  if(!customElements.get('ofc-order-minimum'))customElements.define('ofc-order-minimum',OrderMinimum);
}
